import * as React from "react";
// eslint-disable-next-line no-restricted-imports
import moment from "moment";
import { AppShell, Callout, FullWidthPage } from "@wayflyer/flyui";
import { defineMessages, type IntlShape, useIntl } from "react-intl";
import { useLoaderData } from "react-router";
import { FullScreenLoader } from "shared-ui";

import { MetricTypes } from "../../autogen/metrics";
import { type InsightsLoaderReturn } from "../../loader";
import { getUrls } from "../../urls";
import { type DashboardOutput, getDashboardOutput } from "../../utils/fetch-api";
import { TemporalResolutions } from "../../utils/types";

import { type LtvCacChartType, type LtvCacPeriod, LtvCacScreen } from "./components/screen";
import { LTV_CAC_METRICS } from "./constants";
import { getLtvCacChartData, getLtvCacCustomerChartData } from "./metrics";
import type { LtvCacChartProps } from "./components/chart";

type MetricSelector = {
  [type in LtvCacChartType]: { [period in LtvCacPeriod]: MetricTypes };
};

type PeriodNumBars = {
  [type in LtvCacPeriod]: number;
};

const chartTypeNames = defineMessages({
  aov: { id: "Internal LTV / CAC: AOV Chart Name", defaultMessage: "AOV" },
  frequency: { id: "Internal LTV / CAC: Frequency Chart Name", defaultMessage: "Frequency" },
  ltv: { id: "Internal LTV / CAC: LTV Chart Name", defaultMessage: "LTV" },
});

const periodBarsHighlight: PeriodNumBars = {
  oneMonth: 1,
  threeMonths: 3,
  sixMonths: 6,
  twelveMonths: 12,
  allTime: 0,
};

const metricSelector: MetricSelector = {
  aov: {
    oneMonth: MetricTypes.ONE_MONTH_AOV,
    threeMonths: MetricTypes.THREE_MONTH_AOV,
    sixMonths: MetricTypes.SIX_MONTH_AOV,
    twelveMonths: MetricTypes.TWELVE_MONTH_AOV,
    allTime: MetricTypes.ALLTIME_AOV,
  },
  frequency: {
    oneMonth: MetricTypes.ONE_MONTH_FREQUENCY,
    threeMonths: MetricTypes.THREE_MONTH_FREQUENCY,
    sixMonths: MetricTypes.SIX_MONTH_FREQUENCY,
    twelveMonths: MetricTypes.TWELVE_MONTH_FREQUENCY,
    allTime: MetricTypes.ALLTIME_FREQUENCY,
  },
  ltv: {
    oneMonth: MetricTypes.ONE_MONTH_LTV,
    threeMonths: MetricTypes.THREE_MONTH_LTV,
    sixMonths: MetricTypes.SIX_MONTH_LTV,
    twelveMonths: MetricTypes.TWELVE_MONTH_LTV,
    allTime: MetricTypes.ALLTIME_LTV,
  },
};

const getPeriods = (intl: IntlShape) => [
  { id: "oneMonth", text: intl.formatMessage({ id: "Internal LTV / CAC: One Month", defaultMessage: "One Month" }) },
  {
    id: "threeMonths",
    text: intl.formatMessage({ id: "Internal LTV / CAC: Three Months", defaultMessage: "Three Months" }),
  },
  { id: "sixMonths", text: intl.formatMessage({ id: "Internal LTV / CAC: Six Months", defaultMessage: "Six Months" }) },
  {
    id: "twelveMonths",
    text: intl.formatMessage({ id: "Internal LTV / CAC: Twelve Months", defaultMessage: "Twelve Months" }),
  },
  { id: "allTime", text: intl.formatMessage({ id: "Internal LTV / CAC: All time", defaultMessage: "All time" }) },
];

/**
 * Entry point
 */
const InternalLtvCac = () => {
  const { customerDomain }: InsightsLoaderReturn = useLoaderData();
  const { dashboardDataPath } = getUrls(customerDomain);
  const [isLoadingData, setIsLoadingData] = React.useState(false);
  const [errorData, setErrorData] = React.useState<string | null>(null);
  const [selectedChartType, setSelectedChartType] = React.useState<LtvCacChartType>("ltv");
  const [selectedLtvPeriod, setSelectedLtvPeriod] = React.useState<LtvCacPeriod>("threeMonths");
  const [selectedAovPeriod, setSelectedAovPeriod] = React.useState<LtvCacPeriod>("threeMonths");
  const [selectedFreqPeriod, setSelectedFreqPeriod] = React.useState<LtvCacPeriod>("threeMonths");

  const [tableData, setTableData] = React.useState<DashboardOutput | null>(null);
  const [chartData, setChartData] = React.useState<Optional<LtvCacChartProps, "formatValue"> | null>(null);
  const [customersChartData, setCustomersChartData] = React.useState<Optional<LtvCacChartProps, "formatValue"> | null>(
    null
  );
  const intl = useIntl();

  const fetchData = React.useCallback(() => {
    setIsLoadingData(true);
    setErrorData(null);

    const absoluteStart = moment().subtract(2, "year");
    const absoluteEnd = moment();

    if (!chartData || !tableData) {
      getDashboardOutput(
        dashboardDataPath,
        [
          {
            metrics: LTV_CAC_METRICS,
            filename: "ltv_cac_daily_insights",
            groupBys: [],
          },
        ],
        { start: absoluteStart, end: absoluteEnd },
        (data) => {
          setTableData(data);
          setChartData(
            getLtvCacChartData(
              data,
              selectedChartType,
              intl.formatMessage(chartTypeNames[selectedChartType]),
              periodBarsHighlight[selectedLtvPeriod],
              metricSelector[selectedChartType][selectedLtvPeriod]
            )
          );

          setCustomersChartData(getLtvCacCustomerChartData(data));

          setIsLoadingData(false);
          setErrorData(null);
        },
        null,
        (error) => {
          setIsLoadingData(false);
          setErrorData(error);
        },
        TemporalResolutions.MONTHLY
      );
    } else {
      setChartData(
        getLtvCacChartData(
          tableData,
          selectedChartType,
          intl.formatMessage(chartTypeNames[selectedChartType]),
          periodBarsHighlight[selectedLtvPeriod],
          metricSelector[selectedChartType][selectedLtvPeriod]
        )
      );
      setIsLoadingData(false);
      setErrorData(null);
    }
  }, [dashboardDataPath, selectedChartType, selectedLtvPeriod, chartData, tableData, intl]);

  const handlePeriodChange = (type: LtvCacChartType, period: LtvCacPeriod) => {
    if (!tableData) {
      return;
    }

    switch (type) {
      case "ltv":
        setSelectedLtvPeriod(period);
        break;
      case "aov":
        setSelectedAovPeriod(period);
        break;
      case "frequency":
        setSelectedFreqPeriod(period);
        break;
      default:
        break;
    }

    setChartData(
      getLtvCacChartData(
        tableData,
        selectedChartType,
        intl.formatMessage(chartTypeNames[selectedChartType]),
        periodBarsHighlight[period],
        metricSelector[selectedChartType][period]
      )
    );
  };

  const handleChartTypeChange = (type: LtvCacChartType) => {
    if (!tableData) {
      return;
    }

    setSelectedChartType(type);
    const period = type === "ltv" ? selectedLtvPeriod : type === "aov" ? selectedAovPeriod : selectedFreqPeriod;

    setChartData(
      getLtvCacChartData(
        tableData,
        type,
        intl.formatMessage(chartTypeNames[type]),
        periodBarsHighlight[period],
        metricSelector[type][period]
      )
    );
  };

  React.useEffect(() => {
    if (!chartData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      fetchData();
    }
  }, [fetchData, chartData]);

  const chartTypes = Object.entries(chartTypeNames).map(([key, value]) => {
    return {
      id: key,
      text: intl.formatMessage(value),
    };
  });
  const pageTitle = intl.formatMessage({ id: "Internal LTV / CAC: Page Title", defaultMessage: "Customer Analysis" });

  if (!chartData || !customersChartData) {
    return (
      <AppShell>
        {errorData ? (
          <FullWidthPage title={pageTitle}>
            <Callout variant="critical" description={errorData} />
          </FullWidthPage>
        ) : (
          <FullScreenLoader />
        )}
      </AppShell>
    );
  }

  return (
    <AppShell>
      <LtvCacScreen
        chartDataError={errorData}
        chartDataLoading={isLoadingData}
        mainChart={chartData}
        customerChart={customersChartData}
        pageTitle={pageTitle}
        customerTitle={intl.formatMessage({
          id: "Internal LTV / CAC: Customer Title",
          defaultMessage: "Is customer acquisition scalable?",
        })}
        selectedChartType={selectedChartType}
        chartTypes={chartTypes}
        onChangeChart={handleChartTypeChange}
        onChangePeriod={handlePeriodChange}
        selectedLtvPeriod={selectedLtvPeriod}
        selectedAovPeriod={selectedAovPeriod}
        selectedFreqPeriod={selectedFreqPeriod}
        periods={getPeriods(intl)}
      />
    </AppShell>
  );
};

export default InternalLtvCac;
